/**
 * Flat diff paths -> a real tree.
 *
 * The contract's `BlueprintDiff` is deliberately compact: `added_paths`,
 * `removed_paths` and `changed[]` are *paths*, not a nested structure
 * (`adforge/api/contracts.py`: "the diff between two versions is the core console
 * screen"). Rendering that as a JSON blob would throw away the one thing the review
 * screen exists for — seeing which ad group, which ad, which headline changed.
 *
 * This module parses the path syntax into display segments and builds an ordered tree
 * with rolled-up change counts. It accepts the three spellings a backend might emit
 * (`campaigns[0].ad_groups[2]`, `/campaigns/0/ad_groups/2`, `campaigns.0.ad_groups.2`)
 * so the screen keeps working when W2-C fixes the path dialect.
 */
import type { BlueprintDiff, DiffEntry } from '@/api/types'

export type ChangeKind = 'added' | 'removed' | 'changed' | 'unchanged'

export interface DiffLeaf {
  path: string
  kind: Exclude<ChangeKind, 'unchanged'>
  before: unknown
  after: unknown
}

export interface DiffTreeNode {
  /** Canonical path of this node, e.g. `campaigns[0].ad_groups[2]`. */
  id: string
  /** Display label of the last segment: `ad_groups[2]`, `headlines[3]`, `text`. */
  label: string
  /** Segment key without its index: `ad_groups`. */
  key: string
  /** Array index when this node is an array element, else null. */
  index: number | null
  kind: ChangeKind
  /** Set when this node itself is a change (not just a container of changes). */
  entry: DiffLeaf | null
  children: DiffTreeNode[]
  counts: { added: number; removed: number; changed: number }
  depth: number
}

/** Reading order for the Blueprint document, so the tree follows the brief's shape. */
const KEY_ORDER: readonly string[] = [
  'schema_version',
  'version',
  'tenant_id',
  'project_id',
  'created_from',
  'site_url',
  'services',
  'locations',
  'exclusions',
  'monthly_budget_micros',
  'brand_terms',
  'languages',
  'tone',
  'crawl_snapshot_ref',
  'crawl_snapshot_hash',
  'raw_inputs',
  'campaigns',
  'name',
  'daily_budget_micros',
  'bid_strategy',
  'kind',
  'template_id',
  'template_version',
  'rationale',
  'target_cpa_micros',
  'target_roas',
  'max_cpc_micros',
  'networks',
  'google_search',
  'search_network',
  'content_network',
  'geo_targets',
  'geo_target_constant',
  'country_code',
  'language_targets',
  'ad_schedule',
  'day_of_week',
  'start_time',
  'end_time',
  'bid_modifier',
  'ad_groups',
  'theme',
  'default_bid_micros',
  'keywords',
  'negatives',
  'ads',
  'landing_page',
  'headlines',
  'descriptions',
  'path1',
  'path2',
  'final_url',
  'text',
  'match_type',
  'source',
  'avg_monthly_searches',
  'competition',
  'top_of_page_bid_low_micros',
  'top_of_page_bid_high_micros',
  'pinned_position',
  'generated_by',
  'evidence',
  'slug',
  'template',
  'headline',
  'subheadline',
  'body_sections',
  'heading',
  'body',
  'cta',
  'meta_title',
  'meta_description',
  'deployed_url',
  'build_hash',
  'build_status',
  'shared_negatives',
  'scope',
  'meta',
  'generated_at',
  'generator_version',
  'model_ids',
  'prompt_hashes',
  'policy_lint',
  'notes',
]

function decodePointer(segment: string): string {
  return segment.replace(/~1/g, '/').replace(/~0/g, '~')
}

/** Raw tokens, with numeric tokens kept separate so they can be attached to a key. */
function rawTokens(path: string): string[] {
  const cleaned = path.trim().replace(/^\$/, '').replace(/^\./, '')
  const tokens: string[] = []
  let current = ''
  const push = () => {
    if (current !== '') {
      tokens.push(decodePointer(current))
      current = ''
    }
  }
  for (const ch of cleaned) {
    if (ch === '.' || ch === '/') push()
    else if (ch === '[') push()
    else if (ch === ']') push()
    else current += ch
  }
  push()
  return tokens
}

/**
 * Split a diff path into display segments: a bare numeric token is folded into the
 * preceding key (`campaigns` + `0` -> `campaigns[0]`), which is how array elements are
 * labelled in the tree.
 */
export function parsePath(path: string): string[] {
  const out: string[] = []
  for (const token of rawTokens(path)) {
    const isIndex = /^\d+$/.test(token)
    const last = out.length > 0 ? out[out.length - 1]! : null
    if (isIndex && last !== null && !/\[\d+\]$/.test(last)) {
      out[out.length - 1] = `${last}[${token}]`
    } else if (isIndex) {
      out.push(`[${token}]`)
    } else {
      out.push(token)
    }
  }
  return out
}

export function splitSegment(segment: string): { key: string; index: number | null } {
  const match = /^(.*)\[(\d+)\]$/.exec(segment)
  if (!match) return { key: segment, index: null }
  return { key: match[1]!, index: Number(match[2]) }
}

function makeNode(segment: string, depth: number, parentId: string): DiffTreeNode {
  const { key, index } = splitSegment(segment)
  const id = parentId === '' ? segment : `${parentId}.${segment}`
  return {
    id,
    label: segment,
    key,
    index,
    kind: 'unchanged',
    entry: null,
    children: [],
    counts: { added: 0, removed: 0, changed: 0 },
    depth,
  }
}

function keyRank(key: string): number {
  const index = KEY_ORDER.indexOf(key)
  return index === -1 ? KEY_ORDER.length : index
}

/**
 * Reading order: the document's own key order first (so `daily_budget_micros` comes
 * before `ad_groups`), then array order within one key. Unknown keys sort alphabetically
 * after the known ones.
 */
function compareNodes(a: DiffTreeNode, b: DiffTreeNode): number {
  const aRank = keyRank(a.key)
  const bRank = keyRank(b.key)
  if (aRank !== bRank) return aRank - bRank
  if (a.index !== null && b.index !== null) return a.index - b.index
  if (a.index !== null) return 1
  if (b.index !== null) return -1
  return a.key.localeCompare(b.key)
}

function sortTree(node: DiffTreeNode): void {
  node.children.sort(compareNodes)
  for (const child of node.children) sortTree(child)
}

function rollUp(node: DiffTreeNode): { added: number; removed: number; changed: number } {
  const counts = { added: 0, removed: 0, changed: 0 }
  if (node.entry) counts[node.entry.kind] += 1
  for (const child of node.children) {
    const childCounts = rollUp(child)
    counts.added += childCounts.added
    counts.removed += childCounts.removed
    counts.changed += childCounts.changed
  }
  node.counts = counts
  return counts
}

/** Build the review tree from a `BlueprintDiff` payload. */
export function buildDiffTree(diff: BlueprintDiff): DiffTreeNode {
  const root: DiffTreeNode = {
    id: '',
    label: '',
    key: '',
    index: null,
    kind: 'unchanged',
    entry: null,
    children: [],
    counts: { added: 0, removed: 0, changed: 0 },
    depth: 0,
  }

  const insert = (
    path: string,
    kind: Exclude<ChangeKind, 'unchanged'>,
    before: unknown = null,
    after: unknown = null,
  ) => {
    const segments = parsePath(path)
    if (segments.length === 0) return
    let node = root
    segments.forEach((segment, depth) => {
      let child = node.children.find((c) => c.label === segment)
      if (!child) {
        child = makeNode(segment, depth + 1, node.id)
        node.children.push(child)
      }
      node = child
    })
    node.kind = kind
    node.entry = { path, kind, before, after }
  }

  for (const path of diff.added_paths ?? []) insert(path, 'added', null, null)
  for (const path of diff.removed_paths ?? []) insert(path, 'removed', null, null)
  for (const entry of (diff.changed ?? []) as DiffEntry[]) {
    insert(entry.path, 'changed', entry.before ?? null, entry.after ?? null)
  }

  sortTree(root)
  rollUp(root)
  return root
}

export interface DiffTotals {
  added: number
  removed: number
  changed: number
  total: number
}

export function diffTotals(diff: BlueprintDiff): DiffTotals {
  const added = diff.added_paths?.length ?? 0
  const removed = diff.removed_paths?.length ?? 0
  const changed = diff.changed?.length ?? 0
  return { added, removed, changed, total: added + removed + changed }
}

export function subtreeTotals(node: DiffTreeNode): DiffTotals {
  const { added, removed, changed } = node.counts
  return { added, removed, changed, total: added + removed + changed }
}

/** Every change in a subtree, in display order. */
export function collectLeaves(node: DiffTreeNode): DiffLeaf[] {
  const out: DiffLeaf[] = []
  const walk = (n: DiffTreeNode) => {
    if (n.entry) out.push(n.entry)
    n.children.forEach(walk)
  }
  walk(node)
  return out
}

/**
 * Nodes to render for a given expansion state. Pruning happens here rather than in the
 * renderer so "hide unchanged" is testable without a DOM.
 */
export interface TreeViewOptions {
  expanded: ReadonlySet<string>
  hideUnchanged: boolean
  filter?: ChangeKind | 'all'
}

export interface TreeRow {
  node: DiffTreeNode
  hasChildren: boolean
  expanded: boolean
  /** Depth of a child that is a change under a collapsed branch, for "3 more" hints. */
  hiddenChanges: number
}

export function visibleRows(root: DiffTreeNode, options: TreeViewOptions): TreeRow[] {
  const rows: TreeRow[] = []
  const filter = options.filter ?? 'all'
  const matches = (node: DiffTreeNode): boolean => {
    if (filter === 'all') return true
    if (node.kind === filter) return true
    return node.children.some(matches)
  }
  const walk = (node: DiffTreeNode) => {
    for (const child of node.children) {
      if (!matches(child)) continue
      if (options.hideUnchanged && child.counts.added + child.counts.removed + child.counts.changed === 0) {
        continue
      }
      const hasChildren = child.children.some(matches)
      const isExpanded = options.expanded.has(child.id)
      rows.push({
        node: child,
        hasChildren,
        expanded: isExpanded,
        hiddenChanges: hasChildren && !isExpanded ? child.counts.added + child.counts.removed + child.counts.changed : 0,
      })
      if (hasChildren && isExpanded) walk(child)
    }
  }
  walk(root)
  return rows
}

/** Ids of every node that has at least one change below it — for "expand all". */
export function expandableIds(root: DiffTreeNode): string[] {
  const out: string[] = []
  const walk = (node: DiffTreeNode) => {
    for (const child of node.children) {
      if (child.children.length > 0) {
        out.push(child.id)
        walk(child)
      }
    }
  }
  walk(root)
  return out
}

/**
 * A human-readable one-liner for a diff leaf: which ad group, which ad, which asset.
 * Used as the screen-reader label on each row.
 */
export function describePath(path: string): string {
  const segments = parsePath(path)
  return segments
    .map((segment) => {
      const { key, index } = splitSegment(segment)
      return index === null ? key : `${key} ${index + 1}`
    })
    .join(' › ')
}
